/**
 * Module User Settings Dialog - Dynamic dialog for module-specific user preferences
 */
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { WorkManagementUserSettingsForm } from '../../shared/components/tasks/WorkManagementUserSettingsForm';
import { ChatUserSettingsForm } from '../../shared/components/ai_chat/ChatUserSettingsForm';
import { CrmUserSettingsForm } from '../../modules/admin/components/CrmUserSettingsForm';
import { RecruitingUserSettingsForm } from '../../modules/admin/components/RecruitingUserSettingsForm';

interface ModuleUserSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  moduleId: string;
  moduleName: string;
  moduleColor: string;
}

// Module settings form components
/**
 * Exported for `__tests__/moduleUserSettingsGear.test.ts`, which pins the pairing
 * this registry has with `modules_entities.config.json`: a picker module that
 * declares `hasUserSettings` but no `userSettingsPath` sends its sidebar gear
 * HERE, so a module missing from this map opens an empty dialog.
 *
 * Keys are module CONFIG IDS (`ai_chat`), never a permission name or an
 * `/admin/module-settings/:moduleId` slug (`chat`) — the lookup is
 * `MODULE_USER_SETTINGS_FORMS[currentModule.id]`, so a key spelled any other way
 * can never be reached and tsc types the miss as a valid `undefined`.
 */
export const MODULE_USER_SETTINGS_FORMS: Record<string, React.ComponentType<{ onSuccess?: () => void }>> = {
  tasks: WorkManagementUserSettingsForm,
  ai_chat: ChatUserSettingsForm,
  pipeline: CrmUserSettingsForm,
  recruiting: RecruitingUserSettingsForm,
  // accounting: AccountingUserSettingsForm,
  // ai: AiUserSettingsForm,
};

export function ModuleUserSettingsDialog({
  open,
  onClose,
  moduleId,
  moduleName,
  moduleColor,
}: ModuleUserSettingsDialogProps) {
  const { t } = useTranslation(['common']);

  const SettingsForm = MODULE_USER_SETTINGS_FORMS[moduleId];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="max-w-md"
        style={{ borderTop: `4px solid ${moduleColor}` }}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>
            {moduleName} {t('nav.settings')}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {SettingsForm ? (
            <SettingsForm onSuccess={onClose} />
          ) : (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">
                {t('moduleSettings.userSettingsNotAvailable')}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('app.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
